import { JSONSchema7 } from 'json-schema';

export type BaseEntity = {
    id: string;
    created_at: Date;
    updated_at: Date;
};

export type Entity<T> = {
    [K in keyof T]: T[K];
} & BaseEntity;

export type Organization = Entity<{
    name: string;
}>;

export type User = Entity<{
    name: string;
    email: string;
    organization: Organization;
    requires_legal_document_acceptance: boolean;
    requires_subscription_acceptance: boolean;
}>;

export enum GraderType {
    CODE = 'CODE',
    PROMPT = 'PROMPT',
    BUILT_IN = 'BUILT_IN',
}

export type UnpopulatedGraderGroup = Entity<{
    organization_id: string | null;
    name: string;
    precision_explanation: string | undefined;
    recall_explanation: string | undefined;
    is_archived: boolean;
    is_editable: boolean;
}>;

export type GraderGroup = Entity<{
    organization_id: string | null;
    name: string;
    precision_explanation: string | undefined;
    recall_explanation: string | undefined;
    is_archived: boolean;
    graders: Grader[];
    created_by_user: User | null;
    is_editable: boolean;
}>;

export type Grader = Entity<{
    version: number;
    grader_group_id: string;
    type: GraderType;
    description: string;
    prompt_criteria: string | undefined;
    config_schema?: JSONSchema7;
    code?: string;
    allow_multiple_per_project: boolean;
}>;

export type ProjectGrader = Entity<{
    project_id: string;
    grader: Grader;
    name: string;
    config: Record<string, unknown>;
    minimum_precision: number;
    minimum_recall: number;
}>;

export type ProjectGraderWithGraderGroup = ProjectGrader & {
    grader_group: Omit<GraderGroup, 'graders' | 'created_by_user'>;
    is_latest_version: boolean;
};

export enum ProjectStatus {
    PENDING = 'PENDING',
    READY = 'READY',
    RUNNING = 'RUNNING',
    COMPLETED = 'COMPLETED',
    CANCELED = 'CANCELED',
    ARCHIVED = 'ARCHIVED',
}

export type Project = Entity<{
    version: number;
    project_group_id: string;
    evaluation_preferences: Record<string, unknown>;
    output_schema: JSONSchema7 | null;
    output_description: string | null;
    raw_problem_description: string | null;
    raw_context: string | null;
    recall_requirement: number;
    maximum_cost: number | null;
    maximum_latency: number | null;
    system_designs: string[];
    only_use_system_designs: boolean;
    status: ProjectStatus;
    maximum_experimentation_cost: number;
    clarification_questions?: Record<string, string[]>;
    clarification_updates?: {
        project_description: string;
        project_context: string;
        input_schema: Record<string, unknown>;
        output_schema: Record<string, unknown>;
    };
    data_cleansing_updates?: {
        project_description: string;
        project_context: string;
        input_schema: Record<string, unknown>;
        output_schema: Record<string, unknown>;
    };
    user: User;
    dataset_id?: string;
    updated_at: Date;
}>;

export type BriefProject = Omit<Project, 'user'>;

export type ProjectGroup = Entity<{
    name: string;
    description: string;
    is_archived: boolean;
    user: User;
    projects: BriefProject[];
}>;

export type ToolGroup = Entity<{
    organization_id: string | null;
    name: string;
    type: ToolType;
    tools: Tool[];
    is_archived: boolean;
    created_by_user: User | null;
    is_editable: boolean;
}>;

export type UnpopulatedToolGroup = Entity<{
    organization_id: string | null;
    name: string;
    type: ToolType;
    is_archived: boolean;
    is_editable: boolean;
}>;

export type Tool = Entity<{
    version: number;
    tool_group_id: string;
    description: string;
    base_config: Record<string, unknown>;
    config_schema: JSONSchema7;
    allow_multiple_per_project: boolean;
}>;

export enum ToolType {
    LLM = 'LLM',
    API = 'API',
}

export type ProjectTool = Entity<{
    project_id: string;
    tool: Tool;
    name: string;
    config: Record<string, unknown>;
}>;

export type ProjectToolWithToolGroup = ProjectTool & {
    tool_group: Omit<ToolGroup, 'tools' | 'created_by_user'>;
    is_latest_version: boolean;
};

export type DatasetGroup = Entity<{
    name: string;
    description: string;
    datasets: BriefDataset[];
    created_by_user: User;
    is_archived: boolean;
}>;

export type DatasetItemInputInfo = {
    variable_name: string;
    special_data_type: SpecialDataType | null;
};

type DatasetItemInput = Entity<{
    info: DatasetItemInputInfo;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    raw_data: any | null;
}>;

export type DatasetItem = Entity<{
    inputs: DatasetItemInput[];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output?: any;
    dataset_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    metadata?: Record<string, any>;
}>;

export type Dataset = Entity<{
    version: number;
    infos: DatasetItemInputInfo[];
    input_schema: JSONSchema7;
    output_schema: JSONSchema7 | undefined | null;
    is_latest_version: boolean;
    dataset_group_id: string;
}>;

export type BriefDataset = Entity<{
    version: number;
}>;

export type ImplementationCode = Entity<{
    name: string | null;
    code: string;
    input_variable_names: string[];
    output_variable_name: string;
    description: string;
    order: number;
    code_history: {
        code: string;
        created_at: Date;
    }[];
    experiment_stage_id: string;
}>;

export type PreprocessedData = Entity<{
    variable_name: string;
    description: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any;
}>;

export type ToolImplementation = {
    function_name: string;
    description: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output_schema: any;
    implementation_information: {
        system_prompt: string;
        user_message_template: string;
        output_format:
            | 'STRING'
            | 'JSON'
            | 'ARRAY'
            | 'INTEGER'
            | 'FLOAT'
            | 'BOOLEAN';
        json_schema: JSONSchema7 | null;
        model: string;
        temperature: number;
        cost: number;
        latency: number;
        inference_arguments: string[];
        thinking_enabled: boolean | null;
        reasoning_effort: 'low' | 'medium' | 'high' | null;
        max_thinking_tokens: number | null;
    };
    project_tool: Omit<ProjectToolWithToolGroup, 'is_latest_version'>;
};

export type Evaluation = Entity<{
    avg_precision: number;
    avg_recall: number;
    project_grader: ProjectGraderWithGraderGroup;
    any_answer_recall: number;
    num_items_with_precision_graded: number;
    num_items_with_recall_graded: number;
    items_with_precision_graded_aggregate_score: number;
    items_with_recall_graded_aggregate_score: number;
    created_at: Date;
    updated_at: Date;
}>;

export type Implementation = Entity<{
    name: string;
    input_schema: JSONSchema7;
    output_schema: JSONSchema7;
    implementation_codes: ImplementationCode[];
    preprocessed_data: PreprocessedData[];
    tools: ToolImplementation[];
    evaluations: Evaluation[];
    cost: number | null;
    latency: number | null;
    completed_at: Date | null;
}>;

export type ApiKey = Entity<{
    name: string;
    key_prefix: string;
    key_suffix: string;
    status: 'enabled' | 'disabled';
    created_by_user: User;
    last_used_at: Date | null;
    created_at: Date;
    updated_at: Date;
}>;

export type CreatedApiKey = ApiKey & {
    api_key: string;
};

export enum DeploymentGroupStatus {
    ENABLED = 'enabled',
    DISABLED = 'disabled',
    DELETED = 'deleted',
}

export type DeploymentGroup = Entity<{
    name: string;
    status: DeploymentGroupStatus;
    validate_input: boolean;
    validate_output: boolean;
    created_by_user: User;
    deployments: Deployment[];
}>;

export type Deployment = Entity<{
    deployment_group_id: string;
    rollout_percentage: number; // 0-10000
    version: number;
    implementation_id: string;
}>;

export type TraceGroup = Entity<{
    name: string;
    description: string | null;
    sample_rate: number; // 0-10000
    sampling_percentage: number; // 0-1
    deployment: Deployment | null;
    created_by_user: User;
}>;

export type Trace = Entity<{
    trace_group_id: string;
    start_time: Date | null;
    end_time: Date | null;
    latency: number | null;
    cents_cost: number | null;
    input_data: Record<string, unknown>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    output_data: any | null;
    error: string | null;
    attributes: Record<string, unknown>;
}>;

export type TraceWithSpans = Trace & {
    spans: Span[];
};

export enum DeploymentGroupTimeMetricsType {
    COST = 'cost',
    LATENCY = 'latency',
    CALLS = 'calls',
}

export type DeploymentGroupTimeMetricsData = {
    timestamp: Date;
    value: number;
};

export type DeploymentGroupTimeMetricsSchema = {
    data: DeploymentGroupTimeMetricsData[]; // List of data points for recharts
    interval: 'minute' | 'hour' | 'day' | 'week' | 'month';
    type: DeploymentGroupTimeMetricsType;
};

export type DeploymentGroupAvgMetricsSchema = {
    avg_cost: number; // cents
    avg_latency: number; // milliseconds
};

export enum SpanType {
    LLM = 'llm',
    TOOL = 'tool',
    FUNCTION = 'function',
}

export interface SpanBase {
    name?: string | null;
    trace_id?: string;
    type: SpanType;
    start_time?: Date | null;
    end_time?: Date | null;
    latency?: number | null;
    cents_cost?: number | null;
    order: number;
    input_data?: Record<string, unknown> | null;
    output_data?: unknown | null;
    error?: string | null;
    attributes?: Record<string, unknown> | null;
}

export interface PopulatedSpan extends SpanBase {
    id: string;
    created_at: Date;
    updated_at: Date;
    input_data: Record<string, unknown>;
    output_data: unknown;
    children: Span[];
}

export interface LlmInvocationMessage {
    id: string;
    llm_invocation_id: string;
    template: string;
    populated_message: string;
    role: string; // You might want to narrow this type if you have known roles
    input_variables: Record<string, unknown>;
    order: number;
    image_urls?: string[] | null;
    file_urls?: string[] | null;
    created_at: Date;
}

export interface LLMToolImplementationInformation {
    system_prompt: string;
    user_message_template: string;
    output_format:
        | 'STRING'
        | 'JSON'
        | 'ARRAY'
        | 'INTEGER'
        | 'FLOAT'
        | 'BOOLEAN';
    json_schema: JSONSchema7 | null;
    model: string;
    temperature: number;
    cost: number;
    latency: number;
    inference_arguments: string[];
}

export interface UnpopulatedToolImplementation {
    id: string;
    project_tool_id: string;
    function_name: string;
    description: string;
    output_schema: unknown;
    implementation_information: LLMToolImplementationInformation;
    created_at: Date;
}

export interface LLMSpan extends PopulatedSpan {
    type: SpanType.LLM;
    latency: number;
    cents_cost: number;
    input_tokens: number;
    output_tokens: number;
    temperature: number;
    model: string;
    provider: string;
    messages: LlmInvocationMessage[];
}

export interface ToolSpan extends PopulatedSpan {
    type: SpanType.TOOL;
    tool: UnpopulatedToolImplementation;
    is_cached_result: boolean;
}

export type Span = LLMSpan | ToolSpan | PopulatedSpan;

export enum SpecialDataType {
    CHAT_HISTORY = 'chat_history',
    TEXTRACT = 'textract',
}

export enum SecretType {
    ANTHROPIC_API_KEY = 'ANTHROPIC_API_KEY',
    OPENAI_API_KEY = 'OPENAI_API_KEY',
    AZURE_OPENAI_API_KEY = 'AZURE_OPENAI_API_KEY',
    AZURE_FOUNDRY_API_KEY = 'AZURE_FOUNDRY_API_KEY',
    GEMINI_API_KEY = 'GEMINI_API_KEY',
    FIREWORKS_API_KEY = 'FIREWORKS_API_KEY',
    CUSTOM = 'CUSTOM',
}

export type Secret = Entity<{
    organization_id: string;
    name: string;
    type: SecretType;
    created_by_user: User;
}>;

export type SecretWithValue = Secret & {
    value: string;
};

export type ModelCost = {
    input_cost: number | string;
    output_cost: number | string;
};

export type DatasetItemWithEvaluationItems = DatasetItem & {
    evaluation_items: EvaluationItem[];
    evaluation_data?: unknown;
};

export type EvaluationItem = Entity<{
    evaluation_id: string;
    dataset_item_id: string;
    precision?: number | null;
    recall?: number | null;
    skip: boolean;
    explanation?: string | null;
}>;

export type S3PresignedPost = {
    url: string;
    fields: Record<string, string>;
};

export enum DataCleansingRunStatus {
    PENDING = 'pending',
    PREPARING = 'preparing',
    RUNNING = 'running',
    COMPLETED = 'completed',
    FAILED = 'failed',
    CANCELLED = 'cancelled',
}

export type DataCleansingRun = Entity<{
    dataset_id: string;
    project_id: string;
    status: DataCleansingRunStatus;
    started_at: Date;
    total_items: number;
    items_with_issues: number;
    completed_at: Date | null;
}>;

export type DataCleansingIssue = Entity<{
    result_id: string;
    description: string;
    comments: string | null;
    tag_id: string | null;
    is_resolved: boolean;
    metadata?: Record<string, unknown>;
}>;

export type DataCleansingResult = Entity<{
    run_id: string;
    dataset_item_id: string;
    dataset_item: DatasetItem;
    is_correct: boolean;
    subtask_count: number;
    issues: DataCleansingIssue[];
}>;

export type DataCleansingTag = Entity<{
    run_id: string;
    name: string;
    description: string;
    is_resolved: boolean;
    comments: string | null;
}>;

export type DataCleansingRunWithResults = DataCleansingRun & {
    results: DataCleansingResult[];
    tags: DataCleansingTag[];
};

// Experiment Stage Data Types for detailed evaluation view
export type ExperimentStageInputAndOutputData = {
    experiment_stage_id: string;
    input_data: Record<string, unknown>;
    output_data: Record<string, unknown>;
};

export type ToolInvocationWithToolImplementationAndLLMInvocations = Entity<{
    tool_implementation_id: string;
    experiment_stage_id: string | null;
    dataset_item_id: string | null;
    data: Record<string, unknown>;
    result: unknown;
    data_hash: string;
    is_cached_result: boolean;
    latency_seconds: number | null;
    cost_cents: number | null;
    tool_implementation: UnpopulatedToolImplementation;
    llm_invocations: LlmInvocation[];
}>;

export type LlmInvocation = Entity<{
    tool_invocation_id: string;
    model: string;
    provider: string;
    temperature: number;
    input_tokens: number;
    output_tokens: number;
    latency_seconds: number;
    cents_cost: number;
    messages: LlmInvocationMessage[];
}>;

export type ExperimentStageDataWithToolInvocations =
    ExperimentStageInputAndOutputData & {
        tool_invocations: ToolInvocationWithToolImplementationAndLLMInvocations[];
    };

export enum LegalDocumentType {
    TERMS_OF_SERVICE = 'TERMS_OF_SERVICE',
    PRIVACY_POLICY = 'PRIVACY_POLICY',
    ACCEPTABLE_USE_POLICY = 'ACCEPTABLE_USE_POLICY',
}

export type LegalDocument = {
    display_name: string;
    version: string;
    published_at: Date;
    text: string;
};

export enum BillingCadence {
    MONTHLY = 'MONTHLY',
    YEARLY = 'YEARLY',
}

export enum SubscriptionStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    CANCELED = 'CANCELED',
}

export enum PlanType {
    PILOT = 'PILOT',
    STARTER = 'STARTER',
    PROFESSIONAL = 'PROFESSIONAL',
    ENTERPRISE = 'ENTERPRISE',
}

export type CurrentSubscription = Entity<{
    status: SubscriptionStatus;
    plan_type: PlanType;
    billing_cadence: BillingCadence;
    fee_cents: number;
    user_pays_compute_costs: boolean;
    number_of_projects_per_month: number;
    api_inference_calls_per_month: number;
    traces_per_month: number;
    traces_retention_days: number;
    number_of_users: number;
    auto_renew: boolean;
}>;

export type IndividualPatientData = {
    patient_name: string;
    admitted_date: string | null;
    medicard_amount: number;
    medicard_amount_with_vat: number;
    medicard_professional_fee: number;
    member_code: string | null;
    company_name: string | null;
};

export type DocumentSection = {
    section_name: string;
    page_start: number;
    page_end: number;
    section_text: string;
};

export type MedicardClaim = Entity<{
    control_code: string;
    hospital_name: string;
    medicard_received_date: Date;
    presigned_url: string;
    document_sections: DocumentSection[];
    patient_data: IndividualPatientData[];
    fuzzy_matched_patient_data: IndividualPatientData[] | null;
}>;

// Medication Claim Types
export enum MedicationClaimStatus {
    PENDING_OCR = 'PENDING_OCR',
    EXTRACTING_METADATA = 'EXTRACTING_METADATA',
    EXTRACTING_PATIENTS = 'EXTRACTING_PATIENTS',
    EXTRACTING = 'EXTRACTING',
    ENRICHING = 'ENRICHING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum MedicationPatientStatus {
    PENDING = 'PENDING',
    EXTRACTING_MEDICATIONS = 'EXTRACTING_MEDICATIONS',
    EXTRACTING_DETAILS = 'EXTRACTING_DETAILS',
    ENRICHING_DIAGNOSES = 'ENRICHING_DIAGNOSES',
    ENRICHING_PROCEDURES = 'ENRICHING_PROCEDURES',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum MedicationStatus {
    PENDING = 'PENDING',
    NORMALIZING = 'NORMALIZING',
    MAPPING_GENERIC = 'MAPPING_GENERIC',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum DrugEnrichmentStatus {
    PENDING = 'PENDING',
    ENRICHING = 'ENRICHING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum GenericDrugMappingStatus {
    PENDING = 'PENDING',
    MAPPING = 'MAPPING',
    COMPLETE = 'COMPLETE',
    FAILED = 'FAILED',
}

export enum RoomType {
    PRIVATE = 'private',
    SEMI_PRIVATE = 'semi_private',
    WARD = 'ward',
    ICU = 'icu',
    SUITE = 'suite',
    OBSERVATION = 'observation',
    UNKNOWN = 'unknown',
}

export type GenericDrug = Entity<{
    generic_name: string;
    description: string | null;
}>;

export type NormalizedDrug = Entity<{
    normalized_name: string;
    status: GenericDrugMappingStatus;
    generic_drug: GenericDrug | null;
}>;

export type PatientMedication = Entity<{
    patient_id: string;
    medication_name: string;
    dosage: number | null;
    dosage_unit: string | null;
    quantity: number | null;
    practitioner_name: string | null;
    total_cost: number | null;
    status: MedicationStatus;
    normalized_drug: NormalizedDrug | null;
}>;

export type PatientRoom = Entity<{
    patient_id: string;
    room_type: RoomType | null;
    price_per_day: number;
    number_of_days: number;
    total_cost: number;
}>;

export type PatientDiagnosis = Entity<{
    patient_id: string;
    diagnosis_text: string;
    icd_10_code: string | null;
}>;

export type PatientProcedure = Entity<{
    patient_id: string;
    procedure_text: string;
    cpt_code: string | null;
}>;

export type MedicationClaimDocumentSection = Entity<{
    name: string;
    s3_key: string;
    presigned_url: string;
    page_start: number;
    page_end: number;
    order: number;
    text: string | null;
}>;

export type MedicationClaimDocument = Entity<{
    s3_path: string;
    presigned_url: string;
    original_filename: string | null;
    page_count: number | null;
    sections: MedicationClaimDocumentSection[];
}>;

export type MedicationClaimPatient = Entity<{
    claim_id: string;
    patient_name: string;
    status: MedicationPatientStatus;
    medications: PatientMedication[];
    rooms: PatientRoom[];
    diagnoses: PatientDiagnosis[];
    procedures: PatientProcedure[];
}>;

export type MedicationClaim = Entity<{
    document_id: string;
    organization_id: string;
    control_code: string;
    hospital_name: string | null;
    hospital_address: string | null;
    status: MedicationClaimStatus;
    document: MedicationClaimDocument | null;
    patients: MedicationClaimPatient[];
}>;
